import { useState, useEffect } from 'react'
import { ImageUpload } from './components/ImageUpload'
import { MetadataEditor } from './components/MetadataEditor'
import { ExportManager } from './components/ExportManager'
import { ContextForm } from './components/ContextForm'
import { AuthScreen } from './components/Auth/AuthScreen'
import { CreateAccountAfterPayment } from './components/Auth/CreateAccountAfterPayment'
import { LandingPage } from './components/landing/LandingPage'
import { ABTestProvider } from './contexts/ABTestContext'
import { UserProfile } from './components/Auth/UserProfile'
import { AccountDropdown } from './components/AccountDropdown'
import { AccountLayout, AccountPage } from './components/account/AccountLayout'
import { SubscriptionPage } from './components/account/SubscriptionPage'
import { AccountSettingsPage } from './components/account/AccountSettingsPage'
import { SecurityPage } from './components/account/SecurityPage'
import { HelpSupportPage } from './components/account/HelpSupportPage'
import { PricingPage } from './components/account/PricingPage'
import { MobileNavigation } from './components/MobileNavigation'
import { SubscriptionCard } from './components/billing/SubscriptionCard'
import { PaymentSuccess } from './components/billing/PaymentSuccess'
import { PaymentFailure } from './components/billing/PaymentFailure'
import { BillingTestDashboard } from './components/dev/BillingTestDashboard'
import { DefaultUploadService } from './services/UploadService'
import { AIMetadataGenerator } from './services/MetadataGenerator'
import { AIVisionMetadataGenerator } from './services/AIVisionMetadataGenerator'
import { MarketplaceCSVExporter } from './services/CSVExporter'
import { LocalStorageService } from './services/StorageService'
import { IndexedDBStorageService } from './services/IndexedDBStorageService'
import { SupabaseSyncService } from './services/SupabaseSyncService'
import { useAuth } from './hooks/useAuth'
import { AppIcons, NavIcon, StockTaggerLogoBlack } from './components/icons'
import type { ImageFile } from './contracts/uploadService'
import type { Batch } from './contracts/storageService'
import type { Metadata, ImageContext } from './contracts/metadataGenerator'
import type { MarketplaceType } from './contracts/csvExporter'
import { StorageQuotaError } from './contracts/storageService'

// Initialize basic services
const uploadService = new DefaultUploadService()
const csvExporter = new MarketplaceCSVExporter()

// Use IndexedDB for better storage capabilities, with fallback to localStorage
const createStorageService = () => {
  try {
    // Check if IndexedDB is available
    if ('indexedDB' in window) {
      return { service: new IndexedDBStorageService(), type: 'indexeddb' as const }
    } else {
      console.warn('IndexedDB not available, falling back to localStorage')
      return { service: new LocalStorageService(), type: 'localstorage' as const }
    }
  } catch (error) {
    console.warn('Failed to initialize IndexedDB, falling back to localStorage:', error)
    return { service: new LocalStorageService(), type: 'localstorage' as const }
  }
}

const { service: storageService, type: initialStorageType } = createStorageService()
function App() {
  // Show dev dashboard when on /dev route (development mode only) - CHECK THIS FIRST
  if (window.location.pathname === '/dev') {
    console.log('🔍 Dev route check:', {
      pathname: window.location.pathname,
      isDevPath: window.location.pathname === '/dev',
      isDev: import.meta.env.DEV,
      mode: import.meta.env.MODE
    })

    if (import.meta.env.DEV) {
      console.log('🧪 Showing dev dashboard')
      return <BillingTestDashboard />
    }
  }

  const { user, loading: authLoading } = useAuth()
  const [currentImages, setCurrentImages] = useState<ImageFile[]>([])
  const [imageMetadata, setImageMetadata] = useState<Record<string, Metadata>>({})
  const [activeTab, setActiveTab] = useState<'upload' | 'metadata' | 'export'>('upload')
  const [showAccountPages, setShowAccountPages] = useState(false)
  const [activeAccountPage, setActiveAccountPage] = useState<AccountPage>('subscription')
  const [error, setError] = useState<string | null>(null)
  const [storageError, setStorageError] = useState<StorageQuotaError | null>(null)
  const [notification, setNotification] = useState<{ type: 'success' | 'info' | 'warning' | 'error', message: string } | null>(null)
  const [imageContext, setImageContext] = useState<ImageContext>({})
  const [showMarketplaceValidation, setShowMarketplaceValidation] = useState(false)
  const [storageType] = useState<'indexeddb' | 'localstorage'>(initialStorageType)
  const [showUserProfile, setShowUserProfile] = useState(false)
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false)
  const [showPaymentFailure, setShowPaymentFailure] = useState(false)
  const [showLandingPage, setShowLandingPage] = useState(true)
  const [showAuthScreen, setShowAuthScreen] = useState(false)
  const [showCreateAccount, setShowCreateAccount] = useState(false)
  const [stripeSessionId, setStripeSessionId] = useState<string | null>(null)
  const [authInitialMode, setAuthInitialMode] = useState<'login' | 'signup'>('login')

  // Handle browser back button for landing page navigation
  useEffect(() => {
    const handlePopState = () => {
      // If we're in auth screen and user hits back, go to landing page
      if (showAuthScreen && !user) {
        setShowAuthScreen(false)
        setShowLandingPage(true)
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [showAuthScreen, user])

  // Check for create-account route from Stripe redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const sessionId = urlParams.get('session_id')
    const paymentSuccess = urlParams.get('payment_success')
    const pathname = window.location.pathname
    const href = window.location.href

    console.log('🔍 Route detection - URL:', href)
    console.log('🔍 Route detection - Pathname:', pathname)
    console.log('🔍 Route detection - Session ID:', sessionId)
    console.log('🔍 Route detection - Payment Success:', paymentSuccess)

    // Check for create-account routes
    const paymentComplete = urlParams.get('payment_complete')
    const shouldShowCreateAccount =
      (pathname === '/create-account' && sessionId) ||  // Primary: /create-account?session_id=xxx
      (pathname === '/create-account' && paymentComplete === 'true') ||  // Fallback: manual test URL
      (paymentSuccess === 'true' && sessionId)  // Legacy: root with payment_success

    console.log('🔍 Route detection - Payment Complete:', paymentComplete)
    console.log('🔍 Route detection - Should show create account:', shouldShowCreateAccount)

    if (shouldShowCreateAccount) {
      console.log('🔍 Route detection - Switching to create account screen')
      setStripeSessionId(sessionId || 'temp_session') // Use temp session if no real session_id
      setShowCreateAccount(true)
      setShowLandingPage(false)
      setShowAuthScreen(false)
    } else {
      console.log('🔍 Route detection - Conditions not met', {
        pathname,
        hasSessionId: !!sessionId,
        paymentSuccess,
        paymentComplete
      })
    }
  }, [])

  // Handle stored plan selection after authentication
  useEffect(() => {
    if (user && !authLoading) {
      // Check if there's a stored plan selection from before login
      try {
        const storedPlan = sessionStorage.getItem('selectedPlan')
        if (storedPlan) {
          const planData = JSON.parse(storedPlan)
          console.log('Found stored plan selection:', planData)

          // Clear the stored plan to prevent reprocessing
          sessionStorage.removeItem('selectedPlan')

          // Show success notification that user is logged in
          setNotification({
            type: 'success',
            message: 'Successfully logged in! Proceeding to checkout...'
          })

          // Hide landing page and auth screen after login
          setTimeout(() => {
            setShowLandingPage(false)
            setShowAuthScreen(false)
          }, 1000)
        }
      } catch (error) {
        console.error('Error processing stored plan selection:', error)
      }
    }
  }, [user, authLoading])
  
  // Batch processing state (lifted up from MetadataEditor)
  const [isBatchGenerating, setIsBatchGenerating] = useState(false)
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 })
  
  // AI Configuration from environment variables
  const apiKey = import.meta.env.VITE_OPENAI_API_KEY
  const model = import.meta.env.VITE_OPENAI_MODEL || 'gpt-4o'

  // API Key Security Validation (Development AND Production)
  useEffect(() => {
    // Run validation in both dev and production to catch configuration issues early
    if (apiKey) {
      // Basic security checks
      if (apiKey.length < 20) {
        console.error('❌ OpenAI API key appears to be too short. Please verify your key in Vercel environment variables.')
      }

      if (!apiKey.startsWith('sk-')) {
        console.error('❌ OpenAI API key should start with "sk-". Please verify your key format in Vercel environment variables.')
      }

      if (apiKey.includes('your-api-key') || apiKey.includes('placeholder')) {
        console.error('❌ Placeholder API key detected. Please replace with your actual OpenAI API key in Vercel environment variables.')
      }

      // Check for truncated keys (common Vercel misconfiguration)
      if (apiKey.endsWith('...')) {
        console.error('❌ OpenAI API key appears to be truncated. Ensure the full key is set in Vercel environment variables.')
      }

      // Success validation
      if (apiKey.startsWith('sk-') && apiKey.length > 20 && !apiKey.includes('...')) {
        console.log('✅ OpenAI API key format appears valid')
        console.log(`🔍 API Key preview: ${apiKey.substring(0, 15)}...${apiKey.substring(apiKey.length - 4)}`)
      }
    } else {
      const envType = import.meta.env.PROD ? 'PRODUCTION' : 'DEVELOPMENT'
      console.error(`❌ [${envType}] VITE_OPENAI_API_KEY not found. Image metadata generation will fail.`)
      if (import.meta.env.PROD) {
        console.error('🔧 Fix: Set VITE_OPENAI_API_KEY in Vercel environment variables and redeploy.')
      }
    }
  }, [apiKey])

  const [metadataGenerator] = useState(() => {
    if (apiKey) {
      return new AIVisionMetadataGenerator({
        openaiApiKey: apiKey,
        model: model,
      })
    }
    return new AIMetadataGenerator()
  })

  // Auto-dismiss notifications
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [notification])

  // Auto-dismiss errors
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000)
      return () => clearTimeout(timer)
    }
  }, [error])

  // Auto-dismiss storage errors
  useEffect(() => {
    if (storageError) {
      const timer = setTimeout(() => setStorageError(null), 10000) // Longer for storage errors
      return () => clearTimeout(timer)
    }
  }, [storageError])

  // Check URL parameters for payment results on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const success = urlParams.get('success')
    const canceled = urlParams.get('canceled')
    
    if (success === 'true') {
      setShowPaymentSuccess(true)
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname)
    } else if (canceled === 'true') {
      setShowPaymentFailure(true)
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [])

  const showNotification = (type: 'success' | 'info' | 'warning' | 'error', message: string) => {
    setNotification({ type, message })
  }

  const handleAccountNavigation = (page: AccountPage) => {
    setActiveAccountPage(page)
    setShowAccountPages(true)
  }

  const handleBackToApp = () => {
    setShowAccountPages(false)
  }


  const handleUploadComplete = (images: ImageFile[]) => {
    if (!imageContext.platform) {
      setShowMarketplaceValidation(true)
      // Auto-hide the message after 5 seconds
      setTimeout(() => setShowMarketplaceValidation(false), 5000)
      showNotification('warning', `Please select a Target Platform before proceeding to metadata generation.`)
      return
    }
    
    setCurrentImages(images)
    setImageMetadata({})
    setShowMarketplaceValidation(false)
    showNotification('success', `Successfully processed ${images.length} image${images.length !== 1 ? 's' : ''}`)
    setActiveTab('metadata')
  }

  const handleUploadError = (error: Error) => {
    setError(`Upload failed: ${error.message}`)
  }

  // Helper function to convert StockPlatform to MarketplaceType
  const getMarketplaceFromPlatform = (platform?: string): MarketplaceType => {
    switch (platform) {
      case 'shutterstock':
        return 'shutterstock'
      case 'adobe-stock':
        return 'adobe-stock' // Fixed: Must match CSVExporter marketplace key
      case 'istock':
        return 'istock'
      case 'getty-images':
        return 'shutterstock' // Getty uses similar format to Shutterstock
      default:
        return 'shutterstock' // Default fallback
    }
  }



  const handleMetadataUpdate = (imageId: string, metadata: Metadata) => {
    setImageMetadata(prev => ({
      ...prev,
      [imageId]: metadata,
    }))

  }

  const handleAddMoreImages = async (files: FileList) => {
    if (!imageContext.platform) {
      showNotification('warning', 'Please select a Target Platform before adding more images.')
      return
    }

    console.log(`🔍 Adding more images - Selected ${files.length} files:`)
    for (let i = 0; i < files.length; i++) {
      const file = files.item(i)
      if (file) {
        console.log(`  ${i + 1}. ${file.name} (${file.type}, ${Math.round(file.size / 1024)}KB)`)
      }
    }

    try {
      // First validate files and show which ones will be processed
      const validations = uploadService.validateFiles(files)
      const validFiles = validations.filter(v => v.isValid)
      const invalidFiles = validations.filter(v => !v.isValid)

      console.log(`📋 File validation results:`)
      console.log(`  ✅ Valid files: ${validFiles.length}`)
      console.log(`  ❌ Invalid files: ${invalidFiles.length}`)

      if (invalidFiles.length > 0) {
        console.log(`❌ Invalid files:`)
        invalidFiles.forEach(invalid => {
          console.log(`  - ${invalid.file.name}: ${invalid.errors.map(e => e.message).join(', ')}`)
        })

        // Show user notification about invalid files
        const errorMessages = invalidFiles.map(invalid =>
          `${invalid.file.name}: ${invalid.errors[0]?.message || 'Unknown error'}`
        )
        showNotification('error', `Some files could not be processed:\n${errorMessages.slice(0, 3).join('\n')}${errorMessages.length > 3 ? `\n... and ${errorMessages.length - 3} more` : ''}`)
      }

      if (validFiles.length === 0) {
        showNotification('error', 'No valid files selected. Please select supported image files (JPEG, PNG, WebP, GIF).')
        return
      }

      console.log(`🔄 Processing ${validFiles.length} valid files...`)

      // Process files using the upload service
      const newImages = await uploadService.processFiles(files)

      console.log(`✅ Successfully processed ${newImages.length} images:`)
      newImages.forEach((img, index) => {
        console.log(`  ${index + 1}. ${img.name} (ID: ${img.id})`)
      })

      // Append new images to existing ones
      const previousCount = currentImages.length
      setCurrentImages(prev => {
        const updated = [...prev, ...newImages]
        console.log(`📊 Image count: ${previousCount} → ${updated.length}`)
        return updated
      })

      // Clear metadata for new images (they'll be auto-generated)
      // Keep existing metadata intact

      if (newImages.length !== files.length) {
        showNotification('warning', `Added ${newImages.length} of ${files.length} selected images. Some files may have been skipped due to validation errors.`)
      } else {
        showNotification('success', `Added ${newImages.length} more image${newImages.length !== 1 ? 's' : ''}`)
      }
    } catch (error) {
      console.error('❌ Error in handleAddMoreImages:', error)
      setError(`Failed to add images: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const handleDeleteImage = (imageId: string) => {
    // Remove from current images
    setCurrentImages(prev => prev.filter(img => img.id !== imageId))
    
    // Remove metadata for this image
    setImageMetadata(prev => {
      const updated = { ...prev }
      delete updated[imageId]
      return updated
    })


    showNotification('success', 'Image removed successfully')
  }

  const getExportBatch = (): Batch | null => {
    if (currentImages.length > 0 && Object.keys(imageMetadata).length > 0) {
      return {
        id: `temp-${Date.now()}`,
        name: 'Current Session',
        images: currentImages.map(img => ({
          id: img.id,
          file: img.file,
          metadata: imageMetadata[img.id],
        })),
        createdAt: new Date(),
        size: currentImages.reduce((sum, img) => sum + img.size, 0),
      }
    }

    return null
  }

  const tabs = [
    { id: 'upload' as const, name: 'Upload', icon: AppIcons.FolderOpen },
    { id: 'metadata' as const, name: 'Metadata', icon: AppIcons.Tag },
    { id: 'export' as const, name: 'Export', icon: AppIcons.Download },
  ]

  // Show loading screen while auth initializes
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing...</p>
        </div>
      </div>
    )
  }

  // Show create account flow after payment (before user check)
  if (showCreateAccount && stripeSessionId) {
    return (
      <CreateAccountAfterPayment
        sessionId={stripeSessionId}
        onAccountCreated={() => {
          // Clear the create account state
          setShowCreateAccount(false)
          setStripeSessionId(null)

          // Clear URL parameters
          window.history.replaceState({}, '', '/')

          // Show success notification
          setNotification({
            type: 'success',
            message: 'Welcome to StockTagger.ai! Your account is ready.'
          })
        }}
      />
    )
  }

  // Show landing page or auth screen if not authenticated
  if (!user) {
    if (showLandingPage && !showAuthScreen) {
      return (
        <ABTestProvider>
          <LandingPage
            onSignUp={() => {
              // Add a history entry so back button works
              window.history.pushState({}, '', '/auth')
              setAuthInitialMode('signup')
              setShowLandingPage(false)
              setShowAuthScreen(true)
            }}
            onLogin={() => {
              // Add a history entry so back button works
              window.history.pushState({}, '', '/auth')
              setAuthInitialMode('login')
              setShowLandingPage(false)
              setShowAuthScreen(true)
            }}
            onSelectPlan={(planId, billingCycle) => {
              // Store plan selection for after signup
              sessionStorage.setItem('selectedPlan', JSON.stringify({ planId, billingCycle }))
              // Add a history entry so back button works
              window.history.pushState({}, '', '/auth')
              setAuthInitialMode('signup')
              setShowLandingPage(false)
              setShowAuthScreen(true)
            }}
          />
        </ABTestProvider>
      )
    } else {
      return (
        <AuthScreen
          initialMode={authInitialMode}
          onBackToLanding={() => {
            setShowAuthScreen(false)
            setShowLandingPage(true)
          }}
        />
      )
    }
  }

  // Show account pages when requested
  if (showAccountPages) {
    return (
      <AccountLayout
        activePage={activeAccountPage}
        onPageChange={setActiveAccountPage}
        onBackToApp={handleBackToApp}
      >
        {/* Account page content */}
        {activeAccountPage === 'subscription' && (
          <SubscriptionPage
            onNavigateToPricing={() => {
              setActiveAccountPage('pricing')
            }}
          />
        )}
        {activeAccountPage === 'settings' && <AccountSettingsPage />}
        {activeAccountPage === 'security' && <SecurityPage />}
        {activeAccountPage === 'help' && <HelpSupportPage />}
        {activeAccountPage === 'pricing' && (
          <PricingPage
            onBackToSubscription={() => {
              setActiveAccountPage('subscription')
            }}
          />
        )}
      </AccountLayout>
    )
  }


  return (
    <div className="min-h-screen bg-gray-50 lg:min-h-0 flex flex-col lg:block">
      {/* User Profile Modal */}
      <UserProfile
        isOpen={showUserProfile}
        onClose={() => setShowUserProfile(false)}
      />

      {/* Mobile Navigation */}
      <MobileNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={(tabId) => setActiveTab(tabId as typeof activeTab)}
          user={user}
          currentImages={currentImages}
          imageMetadata={imageMetadata}
          isBatchGenerating={isBatchGenerating}
          batchProgress={batchProgress}
          onNavigateToAccount={(page) => {
            setActiveAccountPage(page)
            setShowAccountPages(true)
          }}
        />

      {/* Desktop Header - Hidden on mobile */}
      <header className="hidden lg:block bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <StockTaggerLogoBlack className="w-12 h-12" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">StockTagger.ai</h1>
                <p className="text-sm text-gray-600">
                  Upload images and videos, generate metadata with AI, and export to stock marketplaces
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {currentImages.length > 0 && (
                <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                  {currentImages.length} image{currentImages.length !== 1 ? 's' : ''}
                </span>
              )}
              {Object.keys(imageMetadata).length > 0 && (
                <span className="text-sm text-green-600 bg-green-100 px-3 py-1 rounded-full">
                  {Object.keys(imageMetadata).length} with metadata
                </span>
              )}
              {isBatchGenerating && (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                  <span className="text-sm text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                    Processing {batchProgress.current}/{batchProgress.total}
                  </span>
                </div>
              )}
              
              {/* Account Dropdown */}
              {user && (
                <AccountDropdown
                  className="flex-shrink-0"
                  onNavigateToAccount={handleAccountNavigation}
                />
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Desktop Navigation Tabs - Hidden on mobile */}
      <div className="hidden lg:block bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors
                  ${activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
                `}
              >
                <NavIcon icon={tab.icon} className="mr-2 w-4 h-4" />
                {tab.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Notifications */}
      {notification && (
        <div className={`
          max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4
        `}>
          <div className={`
            p-3 rounded-lg border flex items-center justify-between
            ${notification.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' :
          notification.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' :
            notification.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' :
              'bg-blue-50 border-blue-200 text-blue-800'}
          `}>
            <span className="text-sm">{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="ml-3 text-lg leading-none"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="text-red-800 text-sm">{error}</span>
              <button
                onClick={() => setError(null)}
                className="text-red-800 text-lg leading-none"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Storage Quota Error Display */}
      {storageError && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <AppIcons.AlertTriangle className="w-5 h-5 text-orange-400 mt-0.5" />
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-orange-800">Storage Quota Exceeded</h3>
                <p className="text-sm text-orange-700 mt-1">{storageError.message}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    onClick={async () => {
                      try {
                        const result = await storageService.cleanup()
                        showNotification('success', `Cleaned up ${result.deletedItems} old batches, freed ${Math.round(result.freedBytes / 1024)}KB`)
                        setStorageError(null)
                      } catch (err) {
                        setError('Failed to cleanup storage')
                      }
                    }}
                    className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded hover:bg-orange-200 transition-colors"
                  >
                    Cleanup Old Batches
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const exportData = await storageService.exportData()
                        const blob = new Blob([exportData], { type: 'application/json' })
                        const url = URL.createObjectURL(blob)
                        const a = document.createElement('a')
                        a.href = url
                        a.download = `image-tagger-backup-${new Date().toISOString().split('T')[0]}.json`
                        a.click()
                        URL.revokeObjectURL(url)
                        showNotification('success', 'Backup exported successfully')
                      } catch (err) {
                        setError('Failed to export backup')
                      }
                    }}
                    className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded hover:bg-orange-200 transition-colors"
                  >
                    Export Backup
                  </button>
                </div>
              </div>
              <button
                onClick={() => setStorageError(null)}
                className="ml-3 text-orange-400 hover:text-orange-600"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl lg:mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-8 pb-4 lg:pb-8 flex-1 lg:flex-none">
        {activeTab === 'upload' && (
              <div className="flex flex-col lg:flex-row gap-6">
                {/* Main Upload Area - First on mobile, flexible on desktop */}
                <div className="flex-1 order-1 lg:order-2">
                  <ImageUpload
                    uploadService={uploadService}
                    onUploadComplete={handleUploadComplete}
                    onUploadError={handleUploadError}
                  />
                  
                  {/* Validation Message */}
                  {showMarketplaceValidation && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-700 text-sm font-medium">
                        Please select a Target Platform before uploading images.
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Context Form - Second on mobile, sidebar on desktop */}
                <div className="w-full lg:w-80 lg:flex-shrink-0 order-2 lg:order-1">
                  <ContextForm
                    context={imageContext}
                    onChange={(context) => {
                      setImageContext(context)
                      setShowMarketplaceValidation(false) // Clear validation when context changes
                    }}
                  />
                </div>
              </div>
            )}


        {activeTab === 'metadata' && (
          <MetadataEditor
            metadataGenerator={metadataGenerator}
            images={currentImages}
            existingMetadata={imageMetadata}
            imageContext={imageContext}
            onMetadataUpdate={handleMetadataUpdate}
            isBatchGenerating={isBatchGenerating}
            batchProgress={batchProgress}
            onBatchGeneratingChange={setIsBatchGenerating}
            onBatchProgressChange={setBatchProgress}
            onAddMoreImages={handleAddMoreImages}
            onDeleteImage={handleDeleteImage}
            onNavigateToExport={() => setActiveTab('export')}
          />
        )}

        {activeTab === 'export' && (
          <ExportManager
            csvExporter={csvExporter}
            batch={getExportBatch()}
            images={currentImages}
            imageMetadata={imageMetadata}
            selectedMarketplace={getMarketplaceFromPlatform(imageContext.platform)}
            sessionName="Current Session"
          />
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-16 mb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-20 lg:pb-8">
          <div className="text-center text-gray-500 text-sm space-y-2">
            <p>StockTagger.ai</p>
            <p>Built with React, TypeScript, and AI-powered metadata generation for images and videos</p>
            <p>
              Made with ❤️ by{' '}
              <a 
                href="https://parchastudios.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline font-medium transition-colors"
              >
                ParchaStudios
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* Payment Result Overlays */}
      {showPaymentSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="max-w-2xl w-full">
            <PaymentSuccess 
              onContinue={() => {
                setShowPaymentSuccess(false)
                setActiveTab('upload')
              }}
            />
          </div>
        </div>
      )}

      {showPaymentFailure && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="max-w-2xl w-full">
            <PaymentFailure
              onRetry={() => {
                setShowPaymentFailure(false)
              }}
              onViewPricing={() => {
                setShowPaymentFailure(false)
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default App
